/**
 * PlaysHubDetail — single public play page with SEO + interaction.
 *
 * - View ping fired on mount (`event=view`).
 * - "Ver jugada completa" CTA pings `event=cta_click` then routes to
 *   /login?next=/playbook/:source_play_id or /register depending on auth.
 * - Likes + comments require auth — we redirect to /login with `?next=` to
 *   come back to the same play after authentication.
 */
import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Heart, MessageSquare, Eye, Share2, ArrowRight, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const absImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API}${url}`;
  return url;
};

function authHeaders() {
  const tok = localStorage.getItem('pm_token');
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

export default function PlaysHubDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [play, setPlay] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  const fetchPlay = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/playshub/plays/${slug}`, { headers: authHeaders() });
      setPlay(r.data);
    } catch (e) {
      setPlay(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  React.useEffect(() => { fetchPlay(); }, [fetchPlay]);

  // Single view ping (debounce double-mount in React StrictMode).
  const pinged = React.useRef(false);
  React.useEffect(() => {
    if (!slug || pinged.current) return;
    pinged.current = true;
    axios.post(`${API}/api/playshub/plays/${slug}/track`, {
      event: 'view',
      referrer: document.referrer || '',
    }).catch(() => {});
  }, [slug]);

  const onLike = async () => {
    if (!user) {
      navigate(`/login?next=/jugadas/${slug}`);
      return;
    }
    try {
      const r = await axios.post(`${API}/api/playshub/plays/${slug}/like`, {}, { headers: authHeaders() });
      setPlay((p) => p ? { ...p, liked_by_me: r.data.liked, likes_count: r.data.likes_count } : p);
    } catch {
      toast.error('No se pudo guardar el like.');
    }
  };

  const onComment = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate(`/login?next=/jugadas/${slug}`);
      return;
    }
    if (draft.trim().length < 2) return;
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/api/playshub/plays/${slug}/comment`,
        { body: draft.trim() },
        { headers: authHeaders() },
      );
      setPlay((p) => p ? { ...p, comments: [...(p.comments || []), r.data], comments_count: (p.comments_count || 0) + 1 } : p);
      setDraft('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo publicar el comentario.');
    } finally {
      setSubmitting(false);
    }
  };

  const onShare = async () => {
    const url = `${window.location.origin}/jugadas/${slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: play.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copiado al portapapeles.');
      }
      axios.post(`${API}/api/playshub/plays/${slug}/track`, { event: 'share' }).catch(() => {});
    } catch { /* user cancelled */ }
  };

  const onCta = () => {
    axios.post(`${API}/api/playshub/plays/${slug}/track`, { event: 'cta_click' }).catch(() => {});
    if (user) {
      navigate(play?.source_play_id ? `/playbook/${play.source_play_id}` : '/playbook');
    } else {
      navigate(`/register?next=/jugadas/${slug}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy text-white grid place-items-center">
        <div className="text-gold font-mono uppercase tracking-widest text-xs animate-pulse">Cargando jugada…</div>
      </div>
    );
  }
  if (!play) {
    return (
      <div className="min-h-screen bg-navy text-white grid place-items-center px-6 text-center">
        <div>
          <div className="font-h text-3xl uppercase mb-3">Jugada no encontrada</div>
          <Link to="/jugadas" className="text-gold font-mono uppercase tracking-widest text-xs underline">← Volver a la biblioteca</Link>
        </div>
      </div>
    );
  }

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: play.title_seo || play.title,
    description: play.summary_partial,
    image: play.image_url || play.image_partial_url,
    datePublished: play.published_at,
    author: { '@type': 'Organization', name: 'PlayMaster IA' },
  };

  return (
    <div className="min-h-screen bg-navy text-white">
      <Helmet>
        <title>{`${play.title_seo || play.title} · PlayMaster IA`}</title>
        <meta name="description" content={play.summary_partial} />
        <link rel="canonical" href={`https://playmasteria.com/jugadas/${play.slug}`} />
        <meta property="og:title" content={play.title_seo || play.title} />
        <meta property="og:description" content={play.summary_partial} />
        {play.image_partial_url && <meta property="og:image" content={absImg(play.image_partial_url)} />}
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      </Helmet>

      <header className="sticky top-0 z-40 bg-navy-deep/95 backdrop-blur border-b border-gold/20">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/jugadas" className="text-gold font-mono uppercase tracking-widest text-xs">← Jugadas</Link>
          <Link to="/" className="font-h text-lg uppercase tracking-widest">PlayMaster IA</Link>
          <Link to={user ? '/dashboard' : '/login'} className="text-slate-300 font-mono uppercase tracking-widest text-xs hover:text-gold">
            {user ? 'Dashboard' : 'Login'}
          </Link>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-4 py-10">
        {/* Title block */}
        <div className="mb-6 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400">
          <span className="px-2 py-1 rounded bg-gold/10 text-gold border border-gold/30">{play.category}</span>
          <span>·</span>
          <span>{fmtDate(play.published_at)}</span>
          {(play.tags || []).map((t) => (
            <span key={t} className="text-slate-400">#{t}</span>
          ))}
        </div>
        <h1 className="font-h text-3xl sm:text-4xl uppercase tracking-tight leading-tight" data-testid="playshub-detail-title">
          {play.title}
        </h1>

        {/* Image — partial (60-70%) */}
        {play.image_partial_url && (
          <div className="relative mt-6 rounded-xl overflow-hidden border border-gold/20 bg-navy-deep aspect-[16/10]">
            <img src={absImg(play.image_partial_url)} alt={play.title} className="w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-navy-deep via-navy-deep/80 to-transparent" />
            <div className="absolute bottom-3 right-3 text-[10px] font-mono uppercase tracking-widest text-gold/80 bg-black/50 px-2 py-1 rounded backdrop-blur">
              Vista parcial
            </div>
          </div>
        )}

        {/* Summary */}
        <p className="mt-6 text-base text-slate-200 leading-relaxed">
          {play.summary_partial}
        </p>

        {/* Full summary (only auth users get it from API; otherwise undefined) */}
        {play.summary_full && (
          <div className="mt-6 rounded-xl border border-gold/30 bg-navy-deep p-5 text-sm text-slate-200 leading-relaxed whitespace-pre-line">
            {play.summary_full}
          </div>
        )}

        {/* CTA — primary action */}
        <div className="mt-8 rounded-xl border-2 border-gold bg-gold/10 p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gold/80 mb-1">Acceso completo</div>
            <div className="font-h text-lg leading-tight">
              {user ? 'Ver la jugada completa en tu Dashboard' : 'Creá tu cuenta gratis para ver la jugada completa'}
            </div>
          </div>
          <button
            data-testid="playshub-cta"
            onClick={onCta}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-gold text-navy-deep font-mono uppercase tracking-widest text-xs hover:bg-gold/90"
          >
            {user ? 'Ver completa' : 'Crear cuenta'} <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Interaction bar */}
        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <button
            data-testid="playshub-like"
            onClick={onLike}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-xs font-mono uppercase tracking-widest ${
              play.liked_by_me ? 'border-rose-400 text-rose-300 bg-rose-500/10' : 'border-gold/30 text-slate-200 hover:border-rose-400'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${play.liked_by_me ? 'fill-rose-400' : ''}`} /> {play.likes_count || 0}
          </button>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gold/20 text-xs font-mono uppercase tracking-widest text-slate-300">
            <MessageSquare className="w-3.5 h-3.5" /> {play.comments_count || 0}
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gold/20 text-xs font-mono uppercase tracking-widest text-slate-300">
            <Eye className="w-3.5 h-3.5" /> {play.views_count || 0}
          </div>
          <button
            data-testid="playshub-share"
            onClick={onShare}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gold/30 text-slate-200 text-xs font-mono uppercase tracking-widest hover:border-gold"
          >
            <Share2 className="w-3.5 h-3.5" /> Compartir
          </button>
        </div>

        {/* Comments */}
        <section className="mt-12">
          <h2 className="font-h text-xl uppercase tracking-tight mb-4">Comentarios ({play.comments_count || 0})</h2>
          <form onSubmit={onComment} className="mb-6">
            <div className="flex items-center gap-2">
              <input
                data-testid="playshub-comment-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={user ? 'Sumá tu comentario…' : 'Iniciá sesión para comentar…'}
                maxLength={600}
                className="flex-1 bg-navy-deep border border-gold/20 rounded-md px-3 py-2 text-sm placeholder:text-slate-500 focus:outline-none focus:border-gold/60"
              />
              <button
                data-testid="playshub-comment-submit"
                type="submit"
                disabled={submitting || draft.trim().length < 2}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-gold text-navy-deep text-xs font-mono uppercase tracking-widest disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" /> Enviar
              </button>
            </div>
          </form>
          {(play.comments || []).length === 0 ? (
            <div className="text-sm text-slate-500 italic">Sé el primero en comentar.</div>
          ) : (
            <ul className="space-y-3" data-testid="playshub-comments">
              {(play.comments || []).map((c) => (
                <li key={c.id} className="rounded-lg border border-white/10 bg-navy-deep px-4 py-3">
                  <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-slate-400">
                    <span className="text-gold/80">{c.user_name}</span>
                    <span>{fmtDate(c.created_at)}</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-100 whitespace-pre-line">{c.body}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </article>
    </div>
  );
}
